import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  forwardRef,
  input,
  signal,
  effect,
  output,
  computed,
  Signal,
  EventEmitter,
  viewChildren
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { DNComment } from '../../../domain/content/content.model';
import { selectCommentsByParentId } from '../../../domain/content/content.selectors';
import { CommentBoxComponent } from '../comment-box/comment-box.component';
import { CreateCommentDTO } from '../comments.component';
import { CommentBubbleComponent } from './comment-bubble/comment-bubble.component';
import { MatButtonModule } from '@angular/material/button';
import { NgClass, NgStyle, SlicePipe } from '@angular/common';

const COMMENTS_PER_LOAD = 5;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-comment-history',
  templateUrl: 'comment-history.component.html',
  styleUrls: ['comment-history.component.scss'],
  imports: [forwardRef(() => CommentBubbleComponent), NgClass, NgStyle, MatButtonModule, SlicePipe]
})
export class CommentHistoryComponent {
  readonly comments = input<DNComment[]>([]);
  commentsInternal = signal<DNComment[]>([]); // Signal interno per i commenti
  replyInternal = signal<DNComment[]>([]);

  // Rimuovi l'output originale poiché ora stai usando quello del genitore
  // readonly loadCommentRepliesEmitter = output<DNComment>();
  // Aggiungi un nuovo input per ricevere l'emitter dal genitore
  readonly replyBus = input<EventEmitter<DNComment>>(null);
  readonly submitCommentBus = input<EventEmitter<CreateCommentDTO>>(null);

  readonly commentBubbleList = viewChildren(CommentBubbleComponent);
  readonly submitCommentEmitter = output<CreateCommentDTO>();

  // UI vars
  commentsShown = signal(COMMENTS_PER_LOAD); // Signal per i commenti visualizzati
  indentLevel: number = 0;
  showMoreLabel: string = this.ts.instant('dottnet.shared.moreContent');

  // Close previous commentBox before opening another one
  lastCommentBoxViewRef: ViewContainerRef = undefined;
  lastCommentBoxRef: ComponentRef<CommentBoxComponent> = undefined;

  constructor(
    private ts: TranslateService,
    private elementRef: ElementRef
  ) {
    // Effetto per aggiornare i commenti interni quando l'input cambia
    effect(() => {
      const commentsValue = this.comments();
      this.commentsInternal.set(commentsValue);
    });
  }


  // Funzione per ottenere le replies di un idContenuto specifico
  getRepliesSignalForId(id: number): Signal<DNComment[]> {
    // Crea un computed Signal che reagisce ai cambiamenti dei commenti interni
    const repliesSignal = computed(() => {
      const commentWithReplies = this.commentsInternal().find(c => c.idContenuto === id);
      return commentWithReplies?.replies || [];
    });
    return repliesSignal;

  }

  // Mostra una commentBox e nasconde l'ultima se già presente
  showReplyBox(showReplyBoxData: {
    viewRef: ViewContainerRef;
    compRef: ComponentRef<CommentBoxComponent>;
  }) {
    const { viewRef, compRef } = showReplyBoxData;

    if (
      this.lastCommentBoxViewRef?.indexOf(this.lastCommentBoxRef?.hostView) !== -1 &&
      this.lastCommentBoxRef !== compRef
    ) {
      this.lastCommentBoxViewRef?.detach(
        this.lastCommentBoxViewRef.indexOf(this.lastCommentBoxRef.hostView)
      );
    }

    this.lastCommentBoxViewRef = viewRef;
    this.lastCommentBoxRef = compRef;
  }

  emitLoadCommentReplies(rootComment: DNComment) {
    // Usa sempre l'emitter del genitore indipendentemente dal livello
    if (this.replyBus()) {
      this.replyBus().emit(rootComment);
    }
  }

  // Modifica questo metodo per usare il bus
  emitSubmitComment(createCommentDTO: CreateCommentDTO) {
    if (this.submitCommentBus()) {
      this.submitCommentBus().emit(createCommentDTO);
    }
  }

  // Mostra più commenti
  showMoreComments() {
    this.commentsShown.update((current) => current + COMMENTS_PER_LOAD);
  }

  // Verifica se ci sono più commenti da mostrare
  areThereMoreComments(): boolean {
    return this.commentsShown() < this.commentsInternal().length;
  }

}
